import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CalendarDays,
  Coins,
  Eye,
  Loader2,
  Megaphone,
  Rocket,
  Search,
  ShieldCheck,
} from "lucide-react";
import { api } from "../api.ts";
import { Auth } from "../components/Auth.tsx";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import { popularLocations } from "../popularFilters.ts";
import { cats } from "../../../src/category.ts";
import type { EventEntry } from "../../../src/fields.ts";
import { adminEmails } from "../../../constants.ts";
import { Input, Select } from "./ui/index.ts";

const labelClass =
  "text-xs font-medium text-gray-300 flex items-center gap-1.5 mb-2";

const eventDate = (entry: EventEntry) =>
  new Date(entry.structured.timestamp.start).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

type CampaignResult = {
  ok: boolean;
  message: string;
};

export const CampaignBuilder = () => {
  const { isLoading, user } = db.useAuth();
  const isAdmin = Boolean(user?.email && adminEmails.includes(user.email));
  const [searchParams] = useSearchParams();

  const [eventQuery, setEventQuery] = useState("");
  const [event, setEvent] = useState<EventEntry | null>(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventError, setEventError] = useState("");

  const [area, setArea] = useState(() => searchParams.get("area") ?? "");
  const [category, setCategory] = useState(
    () => searchParams.get("category") ?? "",
  );
  const [gender, setGender] = useState("");
  const [numPeople, setNumPeople] = useState(20);

  const [matchingUsers, setMatchingUsers] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [balanceAvailable, setBalanceAvailable] = useState<number | null>(null);

  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState<CampaignResult | null>(null);

  useEffect(() => {
    if (!user || isAdmin) return;
    api({ endpoint: "getPromotionBalance", payload: {} })
      .then(({ available }) => setBalanceAvailable(available))
      .catch((e: Error) => setResult({ ok: false, message: e.message }));
  }, [user?.id, isAdmin]);

  const findEvent = async () => {
    setEventLoading(true);
    setEventError("");
    setEvent(null);
    setMatchingUsers(null);
    try {
      const found = await api({
        endpoint: "findCampaignEvent",
        payload: { eventIdOrLink: eventQuery.trim() },
      });
      setEvent(found);
      if (!found) {
        setEventError("No event found. Try the exact name or an f0mo link.");
      }
    } catch (e) {
      setEventError(e instanceof Error ? e.message : "Event lookup failed.");
    } finally {
      setEventLoading(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setMatchingUsers(null);
    try {
      const { matchingUsers: count } = await api({
        endpoint: "getCampaignAudiencePreview",
        payload: {
          ...(event ? { eventIdOrLink: event.id } : {}),
          ...(area ? { area } : {}),
          ...(category ? { category } : {}),
          ...(gender ? { gender } : {}),
        },
      });
      setMatchingUsers(count);
    } catch (e) {
      setResult({
        ok: false,
        message: e instanceof Error ? e.message : "Audience preview failed.",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const launch = async () => {
    setLaunching(true);
    setResult(null);
    try {
      const outcome = await api({
        endpoint: "createEventCampaign",
        payload: {
          eventIdOrLink: event!.id,
          numPeople,
          ...(area ? { area } : {}),
          ...(category ? { category } : {}),
          ...(gender ? { gender } : {}),
        },
      });
      setResult({ ok: outcome.ok, message: outcome.message });
      if (outcome.ok && !isAdmin && outcome.remainingBalance !== undefined) {
        setBalanceAvailable(outcome.remainingBalance);
      }
      if (outcome.ok) setMatchingUsers(null);
    } catch (e) {
      setResult({
        ok: false,
        message: e instanceof Error ? e.message : "Campaign failed.",
      });
    } finally {
      setLaunching(false);
    }
  };

  if (isLoading) return <Spinner />;

  if (!user) {
    return (
      <section className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 space-y-4">
        <div className={sectionHeader}>
          <Megaphone size={20} className="text-purple-400" />
          Promote your event
        </div>
        <p className="text-gray-400 text-sm max-w-xl">
          Reach thousands of tastemakers: match your event with subscribers by
          city, taste, age and gender, then push it straight into their
          WhatsApp. Log in to build a campaign.
        </p>
        <Auth />
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
          <div className={sectionHeader}>
            <Megaphone size={20} className="text-purple-400" />
            Promote your event
          </div>
          {isAdmin && (
            <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              <ShieldCheck size={14} />
              Admin mode — pushes skip billing
            </span>
          )}
        </div>

        {/* Step 1: pick the event */}
        <div className="space-y-3">
          <span className={stepLabel}>Step 1 — Find your event</span>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="text"
              placeholder="Event name or f0mo.com link..."
              value={eventQuery}
              onChange={(e) => setEventQuery(e.currentTarget.value)}
            />
            <button
              type="button"
              onClick={findEvent}
              disabled={eventLoading || !eventQuery.trim()}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white text-sm font-semibold transition shrink-0"
            >
              {eventLoading
                ? <Loader2 size={15} className="animate-spin" />
                : <Search size={15} />}
              Search
            </button>
          </div>
          {eventError && <p className="text-xs text-red-300">{eventError}</p>}
          {event && (
            <div className="bg-black/30 border border-purple-500/25 rounded-xl p-4 flex items-center gap-4 animate-fade-up">
              {event.structured.posterUrl && (
                <img
                  src={event.structured.posterUrl}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover"
                />
              )}
              <div className="min-w-0">
                <div
                  dir="auto"
                  className="text-sm font-semibold text-white truncate"
                >
                  {event.structured.name}
                </div>
                <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                  <CalendarDays size={12} />
                  {eventDate(event)}
                  {event.structured.city ? ` · ${event.structured.city}` : ""}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: audience */}
        <div className="space-y-3 pt-2">
          <span className={stepLabel}>Step 2 — Choose the audience</span>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Area</label>
              <Select
                value={area}
                onChange={(e) => setArea(e.currentTarget.value)}
                className="w-full"
                wrapperClassName="w-full"
              >
                <option value="">Everywhere</option>
                {popularLocations.slice(0, 6).map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <Select
                value={category}
                onChange={(e) => setCategory(e.currentTarget.value)}
                className="w-full"
                wrapperClassName="w-full"
              >
                <option value="">Any interest</option>
                {Object.entries(cats).map(([id, cat]) => (
                  <option key={id} value={id}>
                    {cat.emoji} {cat.display.en}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={labelClass}>Gender</label>
              <Select
                value={gender}
                onChange={(e) => setGender(e.currentTarget.value)}
                className="w-full"
                wrapperClassName="w-full"
              >
                <option value="">Everyone</option>
                <option value="female">Women</option>
                <option value="male">Men</option>
              </Select>
            </div>
            <div>
              <label className={labelClass}>People to reach</label>
              <Input
                type="number"
                min={1}
                max={500}
                value={numPeople}
                onChange={(e) =>
                  setNumPeople(Math.max(1, Number(e.currentTarget.value) || 1))}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={runPreview}
            disabled={previewLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-200 text-xs font-semibold transition"
          >
            {previewLoading
              ? <Loader2 size={14} className="animate-spin" />
              : <Eye size={14} />}
            Preview matching audience
          </button>
          {matchingUsers !== null && (
            <p className="text-xs text-gray-300 animate-fade-in">
              <span className="font-bold text-purple-300">{matchingUsers}</span>
              {" "}
              eligible subscriber{matchingUsers === 1 ? "" : "s"}{" "}
              match this campaign.
            </p>
          )}
        </div>

        {/* Step 3: launch */}
        <div className="pt-2 space-y-3">
          <span className={stepLabel}>Step 3 — Launch</span>
          {!isAdmin && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Coins size={14} className="text-amber-400" />
              {balanceAvailable === null
                ? "Checking your promotion credits..."
                : `You have $${balanceAvailable} available credits ($1 per user pushed).`}
            </div>
          )}
          <button
            type="button"
            onClick={launch}
            disabled={!event || launching}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition shadow-lg shadow-purple-600/25"
          >
            {launching
              ? <Loader2 size={16} className="animate-spin" />
              : <Rocket size={16} />}
            Push "{event?.structured.name ?? "your event"}" to {numPeople}{" "}
            people
          </button>
          {result && (
            <div
              className={`p-4 rounded-lg text-sm flex items-start gap-3 ${
                result.ok
                  ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200"
                  : "bg-red-500/15 border border-red-500/30 text-red-200"
              }`}
            >
              {result.ok ? <Rocket size={18} /> : <AlertCircle size={18} />}
              <span>{result.message}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const sectionHeader = "flex items-center gap-2.5 text-white font-bold text-xl";
const stepLabel =
  "text-xs font-semibold text-gray-300 uppercase tracking-wider block";
